import cx from "classnames";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useMemo } from "react";

import Box from "ds/components/Box";
import useDetectMobile from "hooks/useDetectMobile";

import styles from "./styles.module.css";
import { ColumnId, ColumnMode, DragDropListProps } from "./types";
import DragDropListDroppableContainer from "./DroppableContainer";
import DragDropListOverlay from "./Overlay";
import DragDropListDraggableItem from "./DraggableItem";
import useTwoColumnDND from "./useTwoColumnDND";
import {
  dropAnimation,
  getCoordinateGetter,
  restrictToParentElementWhenUsingKeyboard,
} from "./utils";

const DragDropList = <T extends string, U extends Record<string, unknown> | undefined>({
  columnMode,
  onChange,
  firstColumn,
  secondColumn = [],
  containerClassName,
  itemClassName,
  itemNoBorder,
  hasDroppableCardWrapper,
  emptyState,
  vertical,
  firstColumnTitle,
  firstColumnMinItems = 0,
  secondColumnMinItems = 0,
  secondColumnTitle,
  renderEmptyColumnState,
  verticalAxisOnly,
  disabledItemsInsideContainer,
  renderItemContent,
  itemSize = "small",
  emptyContainerOffset,
}: DragDropListProps<T, U>) => {
  const isMobile = useDetectMobile();

  const minItems = useMemo(() => {
    return {
      [ColumnId.First]: firstColumnMinItems,
      [ColumnId.Second]: secondColumnMinItems,
    };
  }, [firstColumnMinItems, secondColumnMinItems]);

  const {
    isEmpty,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    handleDragOver,
    collisionDetection,
    firstContainerItems,
    secondContainerItems,
    isFirstColumnDisabled,
    isSecondColumnDisabled,
    isDragActive,
    activeElementId,
    handleDragMove,
  } = useTwoColumnDND<T, U>({
    onChange,
    firstColumn,
    secondColumn,
    minItems,
    disabledItemsInsideContainer,
  });

  const coordinateGetter = useMemo(
    () => getCoordinateGetter(vertical, emptyContainerOffset),
    [vertical, emptyContainerOffset]
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        // It disables touch activation on desktop which causes jumping of list items when starting drag
        distance: isMobile ? 10 : 0,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter,
    })
  );

  const allColumnsById: Record<string, U> = useMemo(() => {
    return {
      ...firstColumn.reduce((acc, item) => ({ ...acc, [item.value]: item.props || ({} as U) }), {}),
      ...secondColumn.reduce(
        (acc, item) => ({ ...acc, [item.value]: item.props || ({} as U) }),
        {}
      ),
    };
  }, [firstColumn, secondColumn]);

  if (isEmpty && emptyState) {
    return emptyState;
  }

  return (
    // Key allows us to recalculate size of items (mostly applicable to charts)
    <DndContext
      sensors={sensors}
      onDragEnd={handleDragEnd}
      onDragAbort={handleDragCancel}
      onDragCancel={handleDragCancel}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
      onDragOver={handleDragOver}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      collisionDetection={collisionDetection}
      key={columnMode}
      modifiers={verticalAxisOnly ? [restrictToVerticalAxis] : undefined}
    >
      <Box
        className={cx(
          styles.container,
          columnMode == ColumnMode.Single && styles.singleColumn,
          vertical && styles.vertical,
          isDragActive && styles.isDragActive
        )}
      >
        <SortableContext
          disabled={isFirstColumnDisabled}
          strategy={verticalListSortingStrategy}
          items={firstContainerItems}
        >
          <DragDropListDroppableContainer
            key={ColumnId.First}
            id={ColumnId.First}
            items={firstContainerItems}
            title={firstColumnTitle}
            disabled={isFirstColumnDisabled}
            className={cx(
              vertical && styles.verticalColumn,
              containerClassName,
              columnMode === ColumnMode.Double && !vertical && styles.columnLeft,
              columnMode !== ColumnMode.Double && !vertical && styles.column
            )}
            hasCardWrapper={hasDroppableCardWrapper}
          >
            <Box direction="column">
              {firstContainerItems.map((id, index) => (
                <DragDropListDraggableItem
                  key={id}
                  size={itemSize}
                  noBorder={itemNoBorder}
                  disabled={isFirstColumnDisabled}
                  className={cx(
                    itemClassName,
                    styles.item,
                    index === firstContainerItems.length - 1 && styles.lastItem
                  )}
                  id={id}
                  columnId={ColumnId.First}
                  renderItemProps={allColumnsById[id]}
                  renderItemContent={renderItemContent}
                />
              ))}
              <div
                className={cx(styles.emptyState, firstContainerItems.length === 0 && styles.active)}
              >
                {renderEmptyColumnState?.(isDragActive && !isFirstColumnDisabled, ColumnId.First)}
              </div>
            </Box>
          </DragDropListDroppableContainer>
        </SortableContext>

        {columnMode === ColumnMode.Double && (
          <SortableContext
            disabled={isSecondColumnDisabled}
            strategy={verticalListSortingStrategy}
            items={secondContainerItems}
          >
            <DragDropListDroppableContainer
              key={ColumnId.Second}
              id={ColumnId.Second}
              items={secondContainerItems}
              title={secondColumnTitle}
              disabled={isSecondColumnDisabled}
              className={cx(
                containerClassName,
                !vertical && styles.columnRight,
                vertical && styles.verticalColumn
              )}
              hasCardWrapper={hasDroppableCardWrapper}
            >
              <Box direction="column">
                {secondContainerItems.map((id, index) => (
                  <DragDropListDraggableItem
                    key={id}
                    size={itemSize}
                    noBorder={itemNoBorder}
                    disabled={isSecondColumnDisabled}
                    className={cx(
                      itemClassName,
                      styles.item,
                      index === secondContainerItems.length - 1 && styles.lastItem
                    )}
                    id={id}
                    columnId={ColumnId.Second}
                    renderItemProps={allColumnsById[id]}
                    renderItemContent={renderItemContent}
                  />
                ))}

                <div
                  className={cx(
                    styles.emptyState,
                    secondContainerItems.length === 0 && styles.active
                  )}
                >
                  {renderEmptyColumnState?.(
                    isDragActive && !isSecondColumnDisabled,
                    ColumnId.Second
                  )}
                </div>
              </Box>
            </DragDropListDroppableContainer>
          </SortableContext>
        )}
      </Box>

      <DragOverlay
        dropAnimation={dropAnimation}
        modifiers={[restrictToParentElementWhenUsingKeyboard]}
      >
        {activeElementId && (
          <DragDropListOverlay
            component={renderItemContent({
              id: activeElementId,
              props: allColumnsById[activeElementId],
            })}
            className={itemClassName}
            noBorder={itemNoBorder}
            size={itemSize}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
};

export default DragDropList;
